import { completeGlobalModalQueue } from '@flowus/common/activity-queue';
import { first } from 'lodash-es';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'src/common/components/button';
import { useOpenModal } from 'src/common/components/next-modal';
import { KEY_NEW_SPACE } from 'src/common/const';
import { ID_ASIDE_SPACE } from 'src/hooks/page/use-dnd/helper';
import { getCurrentSpace, useCurrentSpace } from 'src/hooks/space';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { isPersonalSpace } from 'src/hooks/space/use-is-pro-space';
import { useTransaction } from 'src/hooks/use-transaction';
import { Images } from 'src/image';
import { Modals } from 'src/modals';
import { archiveBlock } from 'src/redux/managers/block/archive';
import { SpacePlanType } from 'src/redux/types';
import { $spacesState } from 'src/services/spaces/spaces';
import { $currentUserCache } from 'src/services/user/current-user';
import { ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { elementToGetBoundingClientRect } from 'src/utils/virtualElement';
import { useTocIds } from '../aside/toc/use-toc-id';
import './index.css';

interface Props {
  closeModal: () => void;
}
const ClearTemplate: FC<Props> = (props) => {
  const history = useHistory();
  const currentSpace = useCurrentSpace();
  const tocIds = useTocIds(currentSpace.uuid, $currentUserCache.uuid);
  const transaction = useTransaction();
  const clearTemplate = () => {
    const spaceId = getCurrentSpaceId();
    // 从官网过来复制过来的不清空
    const templateId = localStorage.getItem(`${spaceId}_template_id`);
    const space = getCurrentSpace();
    transaction(() => {
      bizTracker.event('preset_template_click');
      if (isPersonalSpace(space.planType)) {
        archiveBlock(tocIds.person.filter((id) => id !== templateId));
      } else {
        archiveBlock(tocIds.team);
      }
      localStorage.removeItem(`${spaceId}_template_id`);
      history.push(ViewPath.main);
      props.closeModal();
    });
  };
  return (
    <div className="next-modal w-80 p-6 pb-5">
      <div className="text-t1-medium">这里帮你准备了一些模板,</div>
      <div className="text-t1-medium">帮助你快速了解和使用 ~</div>
      <div className="flex items-center justify-between">
        <div className="flex space-x-2.5">
          <Button colorType="secondary" onClick={clearTemplate}>
            清空
          </Button>
          <Button colorType="active" onClick={() => props.closeModal()}>
            好的
          </Button>
        </div>
        <img src={Images.clearTemplate} className="w-[98px]" />
      </div>
    </div>
  );
};

export const useOpenClearTemplate = () => {
  const openModal = useOpenModal();

  return useCallback(() => {
    const spaceId = getCurrentSpaceId();
    if (localStorage.getItem(`${KEY_NEW_SPACE}_${spaceId}`) !== 'true') {
      return;
    }

    const spaces = Object.values($spacesState.getState());
    if (spaces.length === 1 && first(spaces)?.planType === SpacePlanType.freePersonal) {
      return;
    }

    const firstTocItem = document.querySelector(`#${ID_ASIDE_SPACE} [data-block-id]`);

    if (!firstTocItem) {
      return;
    }

    openModal.dropdown({
      modalId: Modals.CLEAR_TEMPLATE,
      autoClose: false,
      keyboard: false,
      popcorn: elementToGetBoundingClientRect(firstTocItem),
      placement: 'right-start',
      offset: [-15, -10],
      arrow: true,
      closeAfterCallBack: () => completeGlobalModalQueue(Modals.CLEAR_TEMPLATE),
      content: (_) => {
        return <ClearTemplate closeModal={_.onCloseModal} />;
      },
    });
    return true;
  }, [openModal]);
};
